<template>
  <div class="platform-admin-layout" :class="{ 'dark-theme': isDarkTheme }">
    <!-- Header -->
    <header class="platform-header">
      <div class="header-content">
        <div class="logo-section">
          <router-link to="/" class="logo-link">
            <img src="@/assets/omind_logo.png" alt="OMIND" class="logo" />
          </router-link>
          <div class="header-divider"></div>
          <h1 class="header-title">
            <i class="pi pi-shield"></i>
            Platform Admin
          </h1>
          <span class="env-badge" :class="'env-badge--' + envInfo.color">
            {{ envInfo.label }}
          </span>
        </div>
        <div class="header-actions">
          <button
            type="button"
            class="theme-toggle"
            :title="isDarkTheme ? 'Switch to Light' : 'Switch to Dark'"
            @click="toggleTheme"
          >
            <i :class="isDarkTheme ? 'pi pi-sun' : 'pi pi-moon'"></i>
          </button>
          <button
            type="button"
            class="change-password-btn"
            data-testid="platform-change-password-open"
            title="Change password"
            @click="openChangePasswordDialog"
          >
            <i class="pi pi-key"></i>
            Change password
          </button>
          <span class="user-badge">
            <i class="pi pi-user"></i>
            {{ userEmail }}
          </span>
          <button
            type="button"
            class="logout-btn"
            data-testid="platform-logout"
            @click="logout"
          >
            <i class="pi pi-sign-out"></i>
            Logout
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <div class="main-container">
      <PlatformSidebar :isDarkTheme="isDarkTheme" />
      <main class="content-area">
        <router-view :isDarkTheme="isDarkTheme" />
      </main>
    </div>

    <Dialog
      v-model:visible="changePasswordVisible"
      :header="changePasswordOtpStep ? 'Verify email code' : 'Change password'"
      modal
      :style="{ width: '420px' }"
      :dismissableMask="true"
      @hide="resetChangePasswordForm"
    >
      <form
        v-if="!changePasswordOtpStep"
        class="change-password-form"
        @submit.prevent="submitChangePassword"
      >
        <div class="form-group">
          <label for="current-password">Current password</label>
          <input
            id="current-password"
            v-model="changePasswordForm.currentPassword"
            type="password"
            data-testid="platform-change-password-current"
            autocomplete="current-password"
            required
            :disabled="changePasswordLoading"
          />
        </div>
        <div class="form-group">
          <label for="new-password">New password</label>
          <input
            id="new-password"
            v-model="changePasswordForm.newPassword"
            type="password"
            data-testid="platform-change-password-new"
            autocomplete="new-password"
            minlength="8"
            required
            :disabled="changePasswordLoading"
          />
        </div>
        <div class="form-group">
          <label for="confirm-password">Confirm new password</label>
          <input
            id="confirm-password"
            v-model="changePasswordForm.confirmPassword"
            type="password"
            data-testid="platform-change-password-confirm"
            autocomplete="new-password"
            minlength="8"
            required
            :disabled="changePasswordLoading"
          />
        </div>
        <div v-if="changePasswordError" class="form-error">
          {{ changePasswordError }}
        </div>
        <div v-if="changePasswordSuccess" class="form-success">
          {{ changePasswordSuccess }}
        </div>
        <div class="dialog-actions">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            text
            :disabled="changePasswordLoading"
            @click="changePasswordVisible = false"
          />
          <Button
            type="submit"
            label="Send verification code"
            data-testid="platform-change-password-submit"
            :loading="changePasswordLoading"
          />
        </div>
      </form>

      <form
        v-else
        class="change-password-form"
        @submit.prevent="submitChangePasswordOtp"
      >
        <p class="otp-hint">
          Enter the 6-digit code sent to your email to confirm the password change.
        </p>
        <div class="form-group">
          <label for="change-password-otp">Verification code</label>
          <input
            id="change-password-otp"
            v-model="changePasswordOtp"
            type="text"
            inputmode="numeric"
            maxlength="6"
            data-testid="platform-change-password-otp"
            autocomplete="one-time-code"
            required
            :disabled="changePasswordLoading"
          />
        </div>
        <div v-if="changePasswordError" class="form-error">
          {{ changePasswordError }}
        </div>
        <div v-if="changePasswordSuccess" class="form-success">
          {{ changePasswordSuccess }}
        </div>
        <div class="dialog-actions">
          <Button
            type="button"
            label="Back"
            severity="secondary"
            text
            :disabled="changePasswordLoading"
            @click="backFromChangePasswordOtp"
          />
          <Button
            type="submit"
            label="Confirm change"
            data-testid="platform-change-password-otp-submit"
            :loading="changePasswordLoading"
          />
        </div>
      </form>
    </Dialog>
  </div>
</template>

<script>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import PlatformSidebar from './PlatformSidebar.vue';
import { getEnvironmentInfo } from '@/helpers/environmentLabel';
import { platformApi } from '@/services/platformApi';

const DEFAULT_SESSION_TIMEOUT_MINUTES = 60;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click'];

export default {
  name: 'PlatformLayout',
  components: {
    PlatformSidebar,
    Dialog,
    Button,
  },
  data() {
    return {
      isDarkTheme: false,
      envInfo: getEnvironmentInfo(),
      changePasswordVisible: false,
      changePasswordLoading: false,
      changePasswordError: null,
      changePasswordSuccess: null,
      changePasswordOtpStep: false,
      changePasswordOtp: '',
      changePasswordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      sessionTimeoutMinutes: DEFAULT_SESSION_TIMEOUT_MINUTES,
      idleTimerId: null,
      activityHandler: null,
    };
  },
  computed: {
    platformUser() {
      const userStr = localStorage.getItem('platform_user');
      if (userStr) {
        try {
          return JSON.parse(userStr);
        } catch (e) {
          return null;
        }
      }
      return null;
    },
    userEmail() {
      return this.platformUser?.email || 'Admin';
    },
    userName() {
      return this.platformUser?.name || 'Admin';
    },
    userRole() {
      return this.platformUser?.role || 'platform_admin';
    },
  },
  mounted() {
    const savedTheme = localStorage.getItem('platform_theme');
    this.isDarkTheme = savedTheme === 'dark';
    this.updateBodyTheme();
    this.initIdleTimeout();
  },
  beforeUnmount() {
    this.teardownIdleTimeout();
  },
  watch: {
    isDarkTheme() {
      this.updateBodyTheme();
    },
  },
  methods: {
    updateBodyTheme() {
      if (this.isDarkTheme) {
        document.body.classList.add('dark-theme', 'p-dark');
      } else {
        document.body.classList.remove('dark-theme', 'p-dark');
      }
    },
    toggleTheme() {
      this.isDarkTheme = !this.isDarkTheme;
      localStorage.setItem('platform_theme', this.isDarkTheme ? 'dark' : 'light');
      this.updateBodyTheme();
    },
    async initIdleTimeout() {
      try {
        const settings = await platformApi.getSettings();
        const minutes = Number(settings?.sessionTimeout);
        if (Number.isFinite(minutes) && minutes > 0) {
          this.sessionTimeoutMinutes = minutes;
        }
      } catch (e) {
        this.sessionTimeoutMinutes = DEFAULT_SESSION_TIMEOUT_MINUTES;
      }

      this.activityHandler = () => this.resetIdleTimer();
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.addEventListener(eventName, this.activityHandler, { passive: true });
      });
      this.resetIdleTimer();
    },
    resetIdleTimer() {
      if (this.idleTimerId) {
        clearTimeout(this.idleTimerId);
      }
      const timeoutMs = this.sessionTimeoutMinutes * 60 * 1000;
      this.idleTimerId = setTimeout(() => {
        this.logout();
      }, timeoutMs);
    },
    teardownIdleTimeout() {
      if (this.idleTimerId) {
        clearTimeout(this.idleTimerId);
        this.idleTimerId = null;
      }
      if (this.activityHandler) {
        ACTIVITY_EVENTS.forEach((eventName) => {
          window.removeEventListener(eventName, this.activityHandler);
        });
        this.activityHandler = null;
      }
    },
    openChangePasswordDialog() {
      this.resetChangePasswordForm();
      this.changePasswordVisible = true;
    },
    resetChangePasswordForm() {
      this.changePasswordForm = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      };
      this.changePasswordError = null;
      this.changePasswordSuccess = null;
      this.changePasswordLoading = false;
      this.changePasswordOtpStep = false;
      this.changePasswordOtp = '';
    },
    backFromChangePasswordOtp() {
      this.changePasswordOtpStep = false;
      this.changePasswordOtp = '';
      this.changePasswordError = null;
      this.changePasswordSuccess = null;
    },
    async submitChangePassword() {
      this.changePasswordError = null;
      this.changePasswordSuccess = null;

      const { currentPassword, newPassword, confirmPassword } = this.changePasswordForm;
      if (!currentPassword || !newPassword) {
        this.changePasswordError = 'Please fill in all password fields.';
        return;
      }
      if (newPassword.length < 8) {
        this.changePasswordError = 'New password must be at least 8 characters.';
        return;
      }
      if (newPassword !== confirmPassword) {
        this.changePasswordError = 'New passwords do not match.';
        return;
      }
      if (currentPassword === newPassword) {
        this.changePasswordError = 'New password must be different from the current password.';
        return;
      }

      this.changePasswordLoading = true;
      try {
        const data = await platformApi.changePassword(currentPassword, newPassword);
        if (data.requiresOtp) {
          this.changePasswordOtpStep = true;
          this.changePasswordOtp = '';
          this.changePasswordSuccess = data.message || 'A verification code has been sent to your email.';
          return;
        }
        this.changePasswordSuccess = data.message || 'Password updated successfully.';
        this.changePasswordForm = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        };
      } catch (err) {
        this.changePasswordError = err.response?.data?.message || 'Could not change password.';
      } finally {
        this.changePasswordLoading = false;
      }
    },
    async submitChangePasswordOtp() {
      this.changePasswordError = null;
      this.changePasswordSuccess = null;

      const otp = String(this.changePasswordOtp || '').trim();
      if (!otp || otp.length !== 6) {
        this.changePasswordError = 'Enter the 6-digit verification code.';
        return;
      }

      const { currentPassword, newPassword } = this.changePasswordForm;
      this.changePasswordLoading = true;
      try {
        const data = await platformApi.confirmChangePassword(otp, currentPassword, newPassword);
        this.changePasswordSuccess = data.message || 'Password updated successfully.';
        this.changePasswordOtpStep = false;
        this.changePasswordOtp = '';
        this.changePasswordForm = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        };
      } catch (err) {
        this.changePasswordError = err.response?.data?.message || 'Could not verify code.';
      } finally {
        this.changePasswordLoading = false;
      }
    },
    logout() {
      this.teardownIdleTimeout();
      localStorage.removeItem('platform_token');
      localStorage.removeItem('platform_refresh_token');
      localStorage.removeItem('platform_user');
      this.$router.push('/platform/login');
    },
  },
};
</script>

<style scoped>
/* ========== Light Theme (Default) ========== */
.platform-admin-layout {
  --bg-primary: #f8f8f8;
  --bg-secondary: #ffffff;
  --bg-tertiary: #f4f4f4;
  --border-color: #e7e7e7;
  --text-primary: #1E2019;
  --text-secondary: #666666;
  --text-muted: #999999;
  --accent-bg: rgba(228, 130, 26, 0.1);
  --accent-border: rgba(228, 130, 26, 0.2);
  --shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  --shadow-hover: 0 4px 12px rgba(0, 0, 0, 0.08);

  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
  font-family: 'AsenPro', sans-serif;
  transition: all 0.3s ease;
}

/* ========== Dark Theme ========== */
.platform-admin-layout.dark-theme {
  --bg-primary: #1E2019;
  --bg-secondary: #2a2a24;
  --bg-tertiary: #353530;
  --border-color: rgba(228, 130, 26, 0.2);
  --text-primary: #f5f5f5;
  --text-secondary: #a8a8a0;
  --text-muted: #777777;
  --accent-bg: rgba(228, 130, 26, 0.15);
  --accent-border: rgba(228, 130, 26, 0.3);
  --shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  --shadow-hover: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.platform-header {
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  box-shadow: var(--shadow);
  position: sticky;
  top: 0;
  z-index: 100;
  transition: all 0.3s ease;
}

.header-content {
  max-width: 1920px;
  margin: 0 auto;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.logo-link {
  display: flex;
  align-items: center;
}

.logo {
  height: 40px;
  width: auto;
}

.header-divider {
  width: 1px;
  height: 30px;
  background: linear-gradient(180deg, transparent, rgba(228, 130, 26, 0.3), transparent);
}

.header-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-title i {
  color: var(--color-orange, #e4821a);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.theme-toggle {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-bg);
  border: 1px solid var(--accent-border);
  border-radius: 50%;
  color: #e4821a;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.theme-toggle:hover {
  transform: rotate(15deg);
  box-shadow: 0 4px 15px rgba(228, 130, 26, 0.3);
}

.change-password-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid var(--accent-border);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.change-password-btn:hover {
  background: var(--accent-bg);
  border-color: #e4821a;
}

.user-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--accent-bg);
  border: 1px solid var(--accent-border);
  border-radius: 9999px;
  color: var(--text-primary);
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.logout-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid rgba(228, 75, 85, 0.3);
  border-radius: 0.5rem;
  color: #e44b55;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.logout-btn:hover {
  background: rgba(228, 75, 85, 0.1);
  border-color: #e44b55;
}

.main-container {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.content-area {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  background: var(--bg-primary);
  transition: all 0.3s ease;
}

.env-badge {
  display: inline-block;
  padding: 0.2rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  line-height: 1.4;
}

.env-badge--red {
  background: rgba(220, 38, 38, 0.15);
  color: #dc2626;
  border: 1px solid rgba(220, 38, 38, 0.4);
}

.env-badge--amber {
  background: rgba(217, 119, 6, 0.15);
  color: #d97706;
  border: 1px solid rgba(217, 119, 6, 0.4);
}

.env-badge--green {
  background: rgba(22, 163, 74, 0.15);
  color: #16a34a;
  border: 1px solid rgba(22, 163, 74, 0.4);
}

.change-password-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.change-password-form .form-group {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.change-password-form label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-primary);
}

.change-password-form input {
  padding: 0.75rem 0.9rem;
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-size: 0.95rem;
}

.change-password-form input:focus {
  outline: none;
  border-color: #e4821a;
}

.form-error {
  color: #e44b55;
  font-size: 0.85rem;
}

.otp-hint {
  margin: 0 0 0.75rem;
  color: var(--text-secondary, #666);
  font-size: 0.9rem;
  line-height: 1.4;
}

.form-success {
  color: #2d8a3e;
  font-size: 0.85rem;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
</style>
